import type { ReportResponse } from "@/react/prop-types";
import { GenerateReportType, ReportType } from "@/react/prop-types";
import type {
   AssignmentListServerFilter,
   CreateAssignmentListReportParams,
} from "./assignment-list-prop-types";
import { FindAssignmentsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/enums/find-assignments";
import { findCustomFieldByIntegrationName } from "@/react/shared/custom-field-utils";
import {
   convertAssignmentDataTableFilters,
   customFieldSortEntityTypeMap,
   entityTypeMap,
   fieldTranslationKeyMap,
   legacyFieldNamesMap,
   mapAssignmentServerFilters,
   sortFieldMaps,
} from "./assignment-list-helpers";
import { parseAssignmentCustomField } from "@/react/shared/helper";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import { GeneratedReportStore } from "@/stores/generated-report-store.core";
import type { ColumnEntityType } from "@laborchart-modules/common";
import type { NotifiableError } from "@bugsnag/js";
import Bugsnag from "@bugsnag/js";
import { BUGSNAG_META_TAB, buildUserData } from "@/lib/utils/bugsnag-content-helper";
import { authManager } from "@/lib/managers/auth-manager";
import { useQuery } from "@tanstack/react-query";
import { streamViewableStatuses } from "../data-table/data-table-utils";
import type { ColorSelectValue } from "../data-table/ColorSelectComponent/ColorSelectColumn";

export async function createAssignmentListReport(
   params: CreateAssignmentListReportParams,
): Promise<ReportResponse | null> {
   const {
      configKey,
      customFields,
      groupId,
      formValues,
      I18n,
      roleOptions,
      isLastNameFirst,
      search,
   } = params;
   let payload: any = {};
   let customFieldId: string | null = null;
   let sortBy: FindAssignmentsSortBy = FindAssignmentsSortBy.ASSIGNMENT_END_DATE;

   try {
      // 1. Get local storage config
      const localStorageTableConfig = JSON.parse(localStorage.getItem(configKey) ?? "");
      const columnState: any[] = localStorageTableConfig.columnState ?? [];
      // 2. Find sort field safely
      const sortField = columnState.find(
         (col: any) => col?.sort !== null && col?.sort !== undefined,
      );
      if (sortField) {
         const { type, field } = parseAssignmentCustomField(sortField.field);
         if (type && field && customFields) {
            const customField = findCustomFieldByIntegrationName(customFields, field);
            if (customField) {
               customFieldId = customField.id;
            }
         }
         sortBy = customFieldId
            ? (customFieldSortEntityTypeMap[type!] as FindAssignmentsSortBy)
            : sortFieldMaps[sortField.field] ?? FindAssignmentsSortBy.ASSIGNMENT_END_DATE;
      }

      // 3. Prepare column headers
      const column_headers = columnState
         .filter((col: any) => !col?.hidden)
         .map((col: any, i: number) => {
            let customField = null;
            let key, name, key_id, meta, base_name;

            const { type, field } = parseAssignmentCustomField(col.field);
            if (type && field && customFields) {
               customField = findCustomFieldByIntegrationName(customFields, field);
            }

            if (col.field.includes("positions_")) {
               const positionId = col.field.split("_").pop();
               const position = roleOptions?.find((pos) => pos.position_id === positionId);
               if (position) {
                  key = "positions";
                  name = position.position_name;
                  key_id = position.position_id;
                  base_name = null;
               }
            } else {
               key = customField ? "custom_fields" : legacyFieldNamesMap[col.field];
               name = customField
                  ? `${customField.entity}'s ${customField.name}`
                  : I18n.t(fieldTranslationKeyMap[col.field]);
               base_name = customField ? customField.integration_name : null;
            }

            if (customField) {
               meta = {
                  field_id: customField.id,
                  field_property: customField.integration_name,
                  field_type: customField.type,
                  field_entity: entityTypeMap[type!] as ColumnEntityType,
               };
            }

            return {
               key,
               name,
               ...(key_id && { key_id }),
               width: Math.round(col.width ?? 100),
               sequence: i,
               base_name,
               sortable: true,
               sub_properties: [],
               ...(customField && { meta }),
            };
         });

      // 4. Prepare filters
      const updatedServerFilters = mapAssignmentServerFilters(
         localStorageTableConfig.serverFilters ?? [],
         customFields ?? [],
      );

      // 5. Prepare payload
      payload = {
         sort_by: sortBy,
         sort_direction: sortField?.sort === "desc" ? Order.DESCENDING : Order.ASCENDING,
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         report_type: ReportType.AssignmentList,
         group_id: groupId,
         limit: 40,
         filters: convertAssignmentDataTableFilters(
            updatedServerFilters as AssignmentListServerFilter[],
         ),
         page_size: {
            height:
               formValues.orientation.id === "portrait"
                  ? formValues.page_size.value.height
                  : formValues.page_size.value.width,
            width:
               formValues.orientation.id === "portrait"
                  ? formValues.page_size.value.width
                  : formValues.page_size.value.height,
         },
         display_last_names_first: isLastNameFirst,
         file_type: formValues.file_type.value,
         column_headers,
         ...(search && { search }),
         ...(customFieldId && { custom_field_id: customFieldId }),
         isCombinedAssignment: true,
      };
      // 6. Generate report
      const report = await GeneratedReportStore.create(GenerateReportType.AssignmentList, payload)
         .payload;

      return report;
   } catch (error) {
      console.error("Error generating Assignment List Report:", error);
      Bugsnag.notify(`error in generating assignment list report` as NotifiableError, (event) => {
         const user = authManager.authedUser();
         if (user) {
            event.addMetadata(
               BUGSNAG_META_TAB.USER_DATA,
               buildUserData(user, authManager.activePermission),
            );
         }
         event.severity = "error";
         event.context = "Helix Assignment List Report Generation";
         event.addMetadata("payload", { data: payload });
      });
      return null;
   }
}

// Fetch statuses using React Query
export function useViewableStatusesOptions(groupId: string): {
   data: ColorSelectValue[];
   isLoading: boolean;
   isError: boolean;
} {
   const {
      data: viewableStatusesOptions = [],
      isLoading,
      isError,
   } = useQuery({
      queryKey: ["viewableStatuses", groupId], // Unique query key
      queryFn: async () => {
         const viewableStatuses = await streamViewableStatuses();
         return viewableStatuses
            .filter((status) => status.id !== "no_status")
            .map((status) => ({
               ...status,
               shape: "circle",
            })) as ColorSelectValue[];
      },
      enabled: !!streamViewableStatuses,
      retry: 1, // Retry once on failure
   });

   return { data: viewableStatusesOptions, isLoading, isError };
}
